// src/pages/cupones/CuponesPage.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { couponAdminApi } from "../../api/couponAdminApi";
import {
  AdminCoupon,
  CouponRequest,
  CouponStatus,
  PageResponse,
  UserCoupon,
} from "../../types";
import { getErrorMessage } from "../../utils/errors";
import { formatDate, todayISO } from "../../utils/format";
import { useDebounce } from "../../hooks/useDebounce";
import { Alert, useFlash } from "../../components/ui/Alert";
import {
  CouponStatusBadge,
  COUPON_STATUS_LABEL,
} from "../../components/ui/Badges";
import Modal from "../../components/ui/Modal";
import Pagination from "../../components/ui/Pagination";
import { UnassignCouponModal } from "../../components/usuarios/UserActionModals";

const PAGE_SIZE = 20;

// Tipos sugeridos (los mismos que usa el backend en la tabla cupones)
const COUPON_TYPES = [
  "cupon_gratis",
  "cupon_bienvenida",
  "cupon_empresa",
  "cupon_promocion",
];

// ── Formulario crear / editar ────────────────────────────────────────────────

interface FormState {
  code: string;
  description: string;
  discountPercent: string;
  quantityAvailable: string;
  type: string;
  startDate: string;
  expirationDate: string;
  active: boolean;
}

function emptyForm(): FormState {
  return {
    code: "",
    description: "",
    discountPercent: "100",
    quantityAvailable: "1",
    type: "cupon_gratis",
    startDate: todayISO(),
    expirationDate: "",
    active: true,
  };
}

function formFromCoupon(c: AdminCoupon): FormState {
  return {
    code: c.code,
    description: c.description ?? "",
    discountPercent: String(c.discountPercent),
    quantityAvailable: String(c.quantityAvailable ?? 0),
    type: c.type ?? "",
    startDate: c.startDate,
    expirationDate: c.expirationDate,
    active: c.active,
  };
}

function CouponFormModal({
  coupon,
  onClose,
  onSaved,
}: {
  coupon: AdminCoupon | null; // null = nuevo
  onClose: () => void;
  onSaved: (coupon: AdminCoupon, message: string) => void;
}) {
  const isEdit = !!coupon;
  const [form, setForm] = useState<FormState>(
    coupon ? formFromCoupon(coupon) : emptyForm(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const validate = (): string | null => {
    if (
      !isEdit &&
      form.code &&
      !/^[A-Za-z0-9_-]{4,30}$/.test(form.code.trim())
    ) {
      return "El código solo puede tener letras, números, guion y guion bajo (entre 4 y 30 caracteres).";
    }
    const discount = Number(form.discountPercent);
    if (
      !form.discountPercent ||
      isNaN(discount) ||
      discount < 1 ||
      discount > 100
    ) {
      return "El descuento debe estar entre 1% y 100%.";
    }
    const quantity = Number(form.quantityAvailable);
    if (
      form.quantityAvailable === "" ||
      !Number.isInteger(quantity) ||
      quantity < 0
    ) {
      return "La cantidad de usos debe ser un número entero mayor o igual a 0.";
    }
    if (!form.expirationDate) return "Indica la fecha de expiración.";
    if (form.startDate && form.expirationDate < form.startDate) {
      return "La fecha de expiración debe ser igual o posterior a la fecha de inicio.";
    }
    if (!isEdit && form.expirationDate < todayISO())
      return "La fecha de expiración no puede ser anterior a hoy.";
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    const payload: CouponRequest = {
      code: isEdit ? undefined : form.code.trim() || undefined,
      description: form.description.trim() || undefined,
      discountPercent: Number(form.discountPercent),
      quantityAvailable: Number(form.quantityAvailable),
      type: form.type.trim() || undefined,
      startDate: form.startDate || undefined,
      expirationDate: form.expirationDate,
      active: form.active,
    };
    setSaving(true);
    setError("");
    try {
      const res = isEdit
        ? await couponAdminApi.actualizar(coupon!.id, payload)
        : await couponAdminApi.crear(payload);
      onSaved(
        res.content,
        isEdit
          ? res.message
          : `Cupón ${res.content.code} creado correctamente.`,
      );
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo guardar el cupón."));
      setSaving(false);
    }
  };

  const discount = Number(form.discountPercent);

  return (
    <Modal
      title={isEdit ? `Editar cupón ${coupon!.code}` : "Nuevo cupón"}
      icon="bi-ticket-perforated-fill"
      iconColor="var(--tp-morado)"
      size="lg"
      onClose={onClose}
      busy={saving}
    >
      <form onSubmit={submit} noValidate>
        {error && <Alert type="error" message={error} />}

        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="coupon-code">
              Código
            </label>
            <input
              id="coupon-code"
              className="form-control"
              value={form.code}
              onChange={(e) =>
                set("code", e.target.value.toUpperCase().replace(/\s/g, ""))
              }
              placeholder="Se genera automáticamente"
              maxLength={30}
              disabled={isEdit}
              style={{ fontFamily: "monospace", fontWeight: 700 }}
            />
            <div className="form-text">
              {isEdit
                ? "El código no se puede cambiar."
                : "Déjalo vacío para generar uno (TOINUP-XXXXXX)."}
            </div>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="coupon-type">
              Tipo
            </label>
            <input
              id="coupon-type"
              className="form-control"
              list="coupon-types"
              value={form.type}
              onChange={(e) => set("type", e.target.value)}
              maxLength={60}
            />
            <datalist id="coupon-types">
              {COUPON_TYPES.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>

          <div className="col-12">
            <label className="form-label" htmlFor="coupon-description">
              Descripción
            </label>
            <input
              id="coupon-description"
              className="form-control"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Ej: Mes gratis para nuevos usuarios"
              maxLength={250}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label" htmlFor="coupon-discount">
              Descuento (%)
            </label>
            <input
              id="coupon-discount"
              type="number"
              min={1}
              max={100}
              step="0.5"
              className="form-control"
              value={form.discountPercent}
              onChange={(e) => set("discountPercent", e.target.value)}
            />
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label" htmlFor="coupon-quantity">
              Usos disponibles
            </label>
            <input
              id="coupon-quantity"
              type="number"
              min={0}
              step={1}
              className="form-control"
              value={form.quantityAvailable}
              onChange={(e) => set("quantityAvailable", e.target.value)}
            />
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label" htmlFor="coupon-start">
              Inicia el
            </label>
            <input
              id="coupon-start"
              type="date"
              className="form-control"
              value={form.startDate}
              onChange={(e) => set("startDate", e.target.value)}
            />
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label" htmlFor="coupon-expiration">
              Vence el
            </label>
            <input
              id="coupon-expiration"
              type="date"
              className="form-control"
              value={form.expirationDate}
              min={isEdit ? undefined : todayISO()}
              onChange={(e) => set("expirationDate", e.target.value)}
            />
          </div>

          <div className="col-12">
            <div className="form-check form-switch">
              <input
                id="coupon-active"
                className="form-check-input"
                type="checkbox"
                checked={form.active}
                onChange={(e) => set("active", e.target.checked)}
              />
              <label
                className="form-check-label"
                htmlFor="coupon-active"
                style={{ fontSize: "0.875rem" }}
              >
                Cupón activo
              </label>
            </div>
          </div>
        </div>

        <div className="tp-alert info mt-3 mb-0">
          <i className="bi bi-info-circle-fill" />
          <span>
            {discount >= 100
              ? "Cupón del 100%: al aplicarlo (o asignarlo desde Usuarios) el usuario queda con Premium hasta la fecha de vencimiento, sin pasar por Mercado Pago."
              : "Cupón con descuento parcial: el usuario lo aplica con el código al pagar su suscripción en la app. No se puede asignar directamente."}
          </span>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn-tp-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button type="submit" className="btn-tp-primary" disabled={saving}>
            {saving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-check-lg" />
            )}
            {isEdit ? "Guardar cambios" : "Crear cupón"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Usuarios que usaron el cupón ─────────────────────────────────────────────

function CouponUsagesModal({
  coupon,
  onClose,
  onChanged,
}: {
  coupon: AdminCoupon;
  onClose: () => void;
  /** Se llama al desasignar para refrescar los contadores de la tabla de cupones */
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [usages, setUsages] = useState<UserCoupon[] | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [unassigning, setUnassigning] = useState<UserCoupon | null>(null);

  const load = useCallback(() => {
    couponAdminApi
      .usos(coupon.id)
      .then((res) => setUsages(res.content))
      .catch((err) =>
        setError(
          getErrorMessage(err, "No se pudieron cargar los usos del cupón."),
        ),
      );
  }, [coupon.id]);

  useEffect(() => {
    load();
  }, [load]);

  // La confirmación reemplaza a este modal mientras está abierta (un solo modal a la vez)
  if (unassigning) {
    return (
      <UnassignCouponModal
        item={unassigning}
        onClose={() => setUnassigning(null)}
        onDone={(_, message) => {
          setUnassigning(null);
          setSuccess(message);
          load();
          onChanged();
        }}
      />
    );
  }

  return (
    <Modal
      title={`Usos del cupón ${coupon.code}`}
      icon="bi-people-fill"
      size="lg"
      onClose={onClose}
    >
      {error && <Alert type="error" message={error} />}
      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => setSuccess("")}
        />
      )}
      {!usages && !error ? (
        <div className="text-center py-4">
          <span
            className="spinner-border"
            style={{ color: "var(--tp-primary)" }}
          />
        </div>
      ) : usages && usages.length === 0 ? (
        <div className="empty-state py-4">
          <i className="bi bi-ticket-perforated" />
          Nadie ha usado este cupón todavía
        </div>
      ) : usages ? (
        <div className="table-responsive">
          <table className="tp-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Fecha de uso</th>
                <th>Origen</th>
                <th style={{ textAlign: "right" }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {usages.map((u) => (
                <tr
                  key={u.userCouponId}
                  className="clickable"
                  onClick={() => navigate(`/usuarios/${u.userId}`)}
                >
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {u.fullName}
                      {u.inEffect && (
                        <span className="badge-estado badge-cupon ms-2">
                          Vigente
                        </span>
                      )}
                    </div>
                    <div className="text-muted-tp">{u.email}</div>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {formatDate(u.usedAt)}
                  </td>
                  <td className="text-muted-tp">
                    {u.assignedByAdmin ? "Panel" : "App"}
                  </td>
                  <td>
                    <div className="table-action-group">
                      {u.removable && (
                        <button
                          className="btn-tp-ghost"
                          style={{ color: "var(--tp-red)" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSuccess("");
                            setUnassigning(u);
                          }}
                          title="Desasignar cupón a este usuario"
                        >
                          <i className="bi bi-x-lg" /> Desasignar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <div className="modal-actions">
        <button className="btn-tp-ghost" onClick={onClose}>
          Cerrar
        </button>
      </div>
    </Modal>
  );
}

// ── PÁGINA ───────────────────────────────────────────────────────────────────

export default function CuponesPage() {
  const [params, setParams] = useSearchParams();
  const flash = useFlash();

  const status = (params.get("status") ?? "") as CouponStatus | "";
  const page = Number(params.get("page") ?? 0) || 0;
  const [search, setSearch] = useState(params.get("q") ?? "");
  const debouncedSearch = useDebounce(search);

  const [data, setData] = useState<PageResponse<AdminCoupon> | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AdminCoupon | "new" | null>(null);
  const [usagesOf, setUsagesOf] = useState<AdminCoupon | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const updateParams = useCallback(
    (changes: Record<string, string | number | undefined>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([k, v]) => {
            if (v === undefined || v === "" || v === 0) next.delete(k);
            else next.set(k, String(v));
          });
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => {
    if ((params.get("q") ?? "") !== debouncedSearch) {
      updateParams({ q: debouncedSearch, page: undefined });
    }
  }, [debouncedSearch, params, updateParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await couponAdminApi.listar({
        search: params.get("q") ?? "",
        status,
        page,
        size: PAGE_SIZE,
      });
      setData(res.content);
    } catch (err) {
      flash.show(
        "error",
        getErrorMessage(err, "No se pudieron cargar los cupones."),
      );
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const onSaved = (coupon: AdminCoupon, message: string) => {
    const wasNew = editing === "new";
    setEditing(null);
    flash.show("success", message);
    if (wasNew) {
      // El nuevo cupón aparece primero en la primera página
      if (page === 0) load();
      else updateParams({ page: undefined });
    } else {
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((c) => (c.id === coupon.id ? coupon : c)),
            }
          : prev,
      );
    }
  };

  const toggleActive = async (coupon: AdminCoupon) => {
    if (
      coupon.active &&
      !window.confirm(
        `¿Desactivar el cupón ${coupon.code}? Ya no se podrá aplicar ni asignar.`,
      )
    ) {
      return;
    }
    setTogglingId(coupon.id);
    try {
      const res = await couponAdminApi.cambiarEstado(coupon.id, !coupon.active);
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((c) =>
                c.id === coupon.id ? res.content : c,
              ),
            }
          : prev,
      );
      flash.show("success", `${res.message.replace(".", "")} ${coupon.code}.`);
    } catch (err) {
      flash.show(
        "error",
        getErrorMessage(err, "No se pudo cambiar el estado del cupón."),
      );
    } finally {
      setTogglingId(null);
    }
  };

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      flash.show("success", `Código ${code} copiado.`);
    } catch {
      flash.show("info", `Código: ${code}`);
    }
  };

  const hasFilters = !!(search || status);

  return (
    <>
      <div className="page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h1 className="page-title">
            <i
              className="bi bi-ticket-perforated-fill me-2"
              style={{ color: "var(--tp-green)", fontSize: "1.2rem" }}
            />
            Cupones
          </h1>
          <p className="page-subtitle">
            Crea y administra los cupones de descuento de la suscripción Premium
            de Toinup
          </p>
        </div>
        <button className="btn-tp-primary" onClick={() => setEditing("new")}>
          <i className="bi bi-plus-lg" /> Nuevo cupón
        </button>
      </div>

      {flash.node}

      <div className="tp-card">
        <div className="tp-card-header">
          <div className="filter-bar w-100">
            <div className="search-box">
              <i className="bi bi-search" />
              <input
                type="search"
                className="form-control"
                placeholder="Buscar por código, descripción o tipo"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="form-select"
              value={status}
              onChange={(e) =>
                updateParams({ status: e.target.value, page: undefined })
              }
              aria-label="Estado del cupón"
            >
              <option value="">Todos los estados</option>
              {(Object.keys(COUPON_STATUS_LABEL) as CouponStatus[]).map((s) => (
                <option key={s} value={s}>
                  {COUPON_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                className="btn-tp-ghost"
                onClick={() => {
                  setSearch("");
                  setParams({}, { replace: true });
                }}
              >
                <i className="bi bi-x-circle" /> Limpiar
              </button>
            )}
          </div>
        </div>

        {loading && !data ? (
          <div className="loading-state">
            <div className="spinner-border" />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-ticket-perforated" />
            {hasFilters
              ? "No hay cupones que coincidan con los filtros"
              : "Aún no hay cupones. Crea el primero."}
          </div>
        ) : (
          <>
            <div
              className="table-responsive"
              style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}
            >
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descripción</th>
                    <th>Descuento</th>
                    <th title="Usos disponibles">Quedan</th>
                    <th>Usados</th>
                    <th>Vigencia</th>
                    <th>Estado</th>
                    <th style={{ textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <button
                          className="btn p-0 border-0 d-inline-flex align-items-center gap-1"
                          onClick={() => copyCode(c.code)}
                          title="Copiar código"
                        >
                          <span className="code-chip">{c.code}</span>
                          <i
                            className="bi bi-copy"
                            style={{
                              color: "var(--tp-muted)",
                              fontSize: "0.8rem",
                            }}
                          />
                        </button>
                      </td>
                      <td>
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 190,
                          }}
                          title={c.description ?? undefined}
                        >
                          {c.description || "—"}
                        </div>
                        {c.type && (
                          <div className="text-muted-tp">{c.type}</div>
                        )}
                      </td>
                      <td>
                        <strong
                          style={{
                            color:
                              c.discountPercent >= 100
                                ? "var(--tp-morado)"
                                : "var(--tp-primary)",
                          }}
                        >
                          {c.discountPercent}%
                        </strong>
                      </td>
                      <td>{c.quantityAvailable ?? 0}</td>
                      <td>
                        <button
                          className="btn-tp-ghost"
                          onClick={() => setUsagesOf(c)}
                          title="Ver usuarios que usaron el cupón"
                        >
                          <i className="bi bi-people" /> {c.timesUsed}
                        </button>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDate(c.startDate)}
                        <div className="text-muted-tp">
                          al {formatDate(c.expirationDate)}
                        </div>
                      </td>
                      <td>
                        <CouponStatusBadge status={c.status} />
                      </td>
                      <td>
                        <div className="table-action-group">
                          <button
                            className="btn-tp-ghost"
                            onClick={() => setEditing(c)}
                            title="Editar"
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            className={
                              c.active ? "btn-tp-ghost" : "btn-tp-success"
                            }
                            style={
                              c.active ? { color: "var(--tp-red)" } : undefined
                            }
                            onClick={() => toggleActive(c)}
                            disabled={togglingId === c.id}
                            title={c.active ? "Desactivar" : "Activar"}
                          >
                            {togglingId === c.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : c.active ? (
                              <i className="bi bi-pause-circle" />
                            ) : (
                              <i className="bi bi-play-circle" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              totalElements={data.totalElements}
              size={data.size}
              onChange={(p) => updateParams({ page: p })}
            />
          </>
        )}
      </div>

      {editing && (
        <CouponFormModal
          coupon={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
      {usagesOf && (
        <CouponUsagesModal
          coupon={usagesOf}
          onClose={() => setUsagesOf(null)}
          onChanged={load}
        />
      )}
    </>
  );
}
